// src/generators/dzial9_prawdopodobienstwo/temat1_kombinatoryka/podtemat1_podstawy_types/DigitOnceBelowNTask.js
// Zadanie (Podstawy): Ile jest liczb trzycyfrowych niewiększych niż N = abc (czyli ≤ N),
// które w zapisie mają dokładnie jedną cyfrę d?  (a∈{1..9}, b,c∈{0..9})
// Metoda: rozłączne przypadki (gdzie stoi d) + zasada mnożenia + ograniczenie ≤ N.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

function randInt(a, b) {
  return Math.floor(Math.random() * (b - a + 1)) + a;
}

// liczba cyfr x z {0,1,...,n-1}, dla których x != excl
function countLessExcl(n, excl) {
  if (n <= 0) return 0;
  return n - (excl < n ? 1 : 0);
}

// liczba cyfr x z {0,1,...,n}, dla których x != excl
function countLeqExcl(n, excl) {
  if (n < 0) return 0;
  return (n + 1) - (excl <= n ? 1 : 0);
}

function computeAnswer(a, b, c, d) {
  // PRZYPADEK A: d w setkach
  let caseA = 0;
  if (d < a) {
    // setki mniejsze od a ⇒ automatycznie ≤ N
    // t,u dowolne ≠ d: 9*9
    caseA = 81;
  } else if (d === a) {
    // setki równe a ⇒ ograniczenia po prefiksie b,c
    const tLess = countLessExcl(b, d);       // t < b, t != d
    const uLeq  = countLeqExcl(c, d);        // u ≤ c, u != d
    caseA = tLess * 9 + (b !== d ? uLeq : 0); // wariant t==b (gdy b!=d)
  }
  // d > a ⇒ 0

  // pomocnicze: setki < a, h ∈ {1..a-1} i h != d
  const Hlt = (a - 1) - (d < a ? 1 : 0);

  // PRZYPADEK B: d w dziesiątkach
  // B1: h < a: Hlt * (u != d -> 9)
  let caseB = Hlt * 9;
  // B2: h == a (i a != d): zależnie od porównania d vs b, teraz dla ≤ N
  if (a !== d) {
    if (d < b)      caseB += 9;                         // całe 9 opcji na u != d
    else if (d === b) caseB += countLeqExcl(c, d);      // u ≤ c, u != d
    // d > b ⇒ 0
  }

  // PRZYPADEK C: d w jedności
  // C1: h < a: Hlt * (t != d -> 9)
  let caseC = Hlt * 9;
  // C2: h == a (i a != d): t < b (t != d) lub t == b (b != d i DODATKOWO d ≤ c)
  if (a !== d) {
    const tLessExcl = countLessExcl(b, d);              // t < b, t != d
    const tEqCond   = (b !== d && d <= c) ? 1 : 0;      // t==b ⇒ potrzebne d ≤ c
    caseC += tLessExcl + tEqCond;
  }

  return caseA + caseB + caseC;
}

export function generateTask() {
  // losujemy N = abc (N trzycyfrowe; dla odmiany unikamy pełnej setki)
  let a = randInt(1, 9);
  let b = randInt(0, 9);
  let c = randInt(0, 9);
  if (b === 0 && c === 0) {
    // jeśli trafiliśmy pełną setkę, "odsuń" losowo
    Math.random() < 0.5 ? (b = randInt(1, 9)) : (c = randInt(1, 9));
  }
  const d = randInt(1, 9); // cyfra interesująca (bez 0, by uniknąć wiodącego zera)
  const answer = computeAnswer(a, b, c, d);
  return {
    type: "DigitOnceBelowNTask",
    data: { a, b, c, d, answer },
  };
}

export function renderTask({ data }) {
  const { a, b, c, d } = data;
  const N = 100 * a + 10 * b + c;
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Ile jest liczb trzycyfrowych <strong>niewiększych niż</strong>{" "}
        <InlineMath math={`${N}`} />{" "}, które w swoim zapisie mają{" "}
        <strong>dokładnie jedną</strong> cyfrę <InlineMath math={`${d}`} />?
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
      placeholder="Wpisz liczbę"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") {
    return { correct: false, message: "Podaj liczbę." };
  }
  const given = parseInt(userAnswer.raw, 10);
  if (Number.isNaN(given)) {
    return { correct: false, message: "Podaj liczbę całkowitą." };
  }
  const correct = given === data.answer;
  userAnswer.correctAnswer = data.answer;
  return { correct, message: correct ? "Dobrze!" : "Spróbuj ponownie." };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.answer}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`${userAnswer.raw}`} />;
}

export function renderExplanation({ data }) {
  const { a, b, c, d, answer } = data;

  const Hlt          = (a - 1) - (d < a ? 1 : 0);  // h ∈ {1..a-1}\{d}
  const tLess_dSetki = countLessExcl(b, d);        // t < b, t != d
  const uLeq_dSetki  = countLeqExcl(c, d);         // u ≤ c, u != d

  // case A (setki = d)
  let caseA = 0;
  if (d < a) caseA = 81;
  else if (d === a) caseA = tLess_dSetki * 9 + (b !== d ? uLeq_dSetki : 0);

  // case B (dziesiątki = d)
  let caseB = Hlt * 9;
  if (a !== d) {
    if (d < b)      caseB += 9;
    else if (d === b) caseB += countLeqExcl(c, d);
  }

  // case C (jedności = d)
  let caseC = Hlt * 9;
  if (a !== d) {
    const tLess_excl = countLessExcl(b, d);
    const tEqCond    = (b !== d && d <= c) ? 1 : 0; // ≤ zamiast <
    caseC += tLess_excl + tEqCond;
  }

  const N = 100 * a + 10 * b + c;
  const setHundredsLatex = `\\{1,2,\\ldots, ${a - 1}\\}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p><strong>Krok 1. Ograniczenia od ≤ N.</strong></p>
      <p>
        Mamy <InlineMath math={`${N}`} /> z cyframi{" "}
        <InlineMath math={`a=${a},\\; b=${b},\\; c=${c}`} />.
        Dla liczb trzycyfrowych{" "}
        <InlineMath math={`\\le\\, ${N}`} /> cyfra setek należy do{" "}
        <InlineMath math={setHundredsLatex} />.
      </p>

      <p><strong>Krok 2. Rozbij na przypadki (gdzie stoi cyfra <InlineMath math={`${d}`} />?).</strong></p>
      <ol>
        <li><em>Setki = <InlineMath math={`${d}`} /></em></li>
        <li><em>Dziesiątki = <InlineMath math={`${d}`} /></em></li>
        <li><em>Jedności = <InlineMath math={`${d}`} /></em></li>
      </ol>

      <p><strong>Krok 3. Policz możliwości w każdym przypadku (mnożenie + ograniczenia prefiksowe).</strong></p>
      <ul>
        <li>
          <em>Setki = {d}:</em>{" "}
          {d < a ? (
            <>
              setki mniejsze od <InlineMath math={`${a}`} /> ⇒ dowolne dziesiątki i
              jedności <em>różne od</em> <InlineMath math={`${d}`} />:
              <BlockMath math={`9\\cdot 9 = 81`} />
            </>
          ) : d === a ? (
            <>
              równe prefiksowi: rozważ t (dziesiątki) i u (jedności):
              <ul>
                <li>
                  <InlineMath math={`t < ${b}`} /> i{" "}
                  <InlineMath math={`t \\neq ${d}`} />:{" "}
                  <InlineMath math={`${tLess_dSetki}`} /> opcji, a{" "}
                  <InlineMath math={`u \\neq ${d}`} /> ⇒{" "}
                  <InlineMath math={`9`} /> opcji.
                </li>
                <li>
                  <InlineMath math={`t = ${b}`} /> (gdy{" "}
                  <InlineMath math={`${b} \\neq ${d}`} />): wymagamy{" "}
                  <InlineMath math={`u \\le ${c}`} /> i{" "}
                  <InlineMath math={`u \\neq ${d}`} /> ⇒{" "}
                  <InlineMath math={`${uLeq_dSetki}`} /> opcji.
                </li>
              </ul>
              <BlockMath math={`${tLess_dSetki}\\cdot 9\\; +\\; (${b}\\neq ${d}?${uLeq_dSetki}:0)\\;=\\; ${caseA}`} />
            </>
          ) : (
            <>niemożliwe (dałoby liczbę &gt; <InlineMath math={`${N}`} />).</>
          )}
        </li>

        <li>
          <em>Dziesiątki = {d}:</em>
          <ul>
            <li>
              <InlineMath math={`h < ${a}`} /> i{" "}
              <InlineMath math={`h \\neq ${d}`} />:{" "}
              <InlineMath math={`${Hlt}`} /> możliwości na setki, jedności{" "}
              <InlineMath math={`\\neq ${d}`} /> ⇒{" "}
              <InlineMath math={`9`} /> opcji.
            </li>
            <li>
              <InlineMath math={`h = ${a}`} /> (gdy{" "}
              <InlineMath math={`${a} \\neq ${d}`} />):{" "}
              {d < b ? (
                <>
                  bo <InlineMath math={`${d} < ${b}`} /> ⇒ całe{" "}
                  <InlineMath math={`9`} /> opcji na jedności.
                </>
              ) : d === b ? (
                <>
                  wymagamy <InlineMath math={`u \\le ${c}`} /> i{" "}
                  <InlineMath math={`u \\neq ${d}`} /> ⇒{" "}
                  <InlineMath math={`${countLeqExcl(c, d)}`} /> opcji.
                </>
              ) : (
                <>niemożliwe.</>
              )}
            </li>
          </ul>
          <BlockMath math={`${Hlt}\\cdot 9\\; +\\; \\text{(wariant dla }h=${a}\\text{)}\\;=\\; ${caseB}`} />
        </li>

        <li>
          <em>Jedności = {d}:</em>
          <ul>
            <li>
              <InlineMath math={`h < ${a}`} /> i{" "}
              <InlineMath math={`h \\neq ${d}`} />:{" "}
              <InlineMath math={`${Hlt}`} /> możliwości na setki, dziesiątki{" "}
              <InlineMath math={`\\neq ${d}`} /> ⇒{" "}
              <InlineMath math={`9`} /> opcji.
            </li>
            <li>
              <InlineMath math={`h = ${a}`} /> (gdy{" "}
              <InlineMath math={`${a} \\neq ${d}`} />): dziesiątki t:
              <ul>
                <li>
                  <InlineMath math={`t < ${b}`} /> i{" "}
                  <InlineMath math={`t \\neq ${d}`} />:{" "}
                  <InlineMath math={`${countLessExcl(b, d)}`} /> opcji,
                </li>
                <li>
                  <InlineMath math={`t = ${b}`} /> (gdy{" "}
                  <InlineMath math={`${b} \\neq ${d}`} />): wymagamy{" "}
                  <InlineMath math={`${d} \\le ${c}`} /> ⇒ 1 opcja (w p.p. 0).
                </li>
              </ul>
            </li>
          </ul>
          <BlockMath math={`${Hlt}\\cdot 9\\; +\\; \\big(${countLessExcl(b, d)} + (${b}\\neq ${d}\\wedge ${d}\\le${c}?1:0)\\big)\\;=\\; ${caseC}`} />
        </li>
      </ul>

      <p><strong>Krok 4. Zasada dodawania.</strong></p>
      <BlockMath math={`${caseA} \\; + \\; ${caseB} \\; + \\; ${caseC} \\;=\\; ${answer}`} />
    </div>
  );
}
